import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Check, Send, CheckCircle2,
} from "lucide-react";
import { GROUPS, ACTIVITIES } from "@/lib/activityCatalog";

const QUESTION_DEFS = {
  tavoli_count: { type: "number", label: "Quanti tavoli?", ph: "es. 12" },
  sale_count: { type: "number", label: "Quante sale?", ph: "es. 2" },
  comande_cucina: { type: "bool", label: "Stampa comande in cucina" },
  comande_bar: { type: "bool", label: "Stampa comande al bar" },
  preconto: { type: "bool", label: "Stampante preconto" },
  tablet_count: { type: "number", label: "Quanti tablet / palmari per i camerieri?", ph: "es. 3" },
  postazioni_count: { type: "number", label: "Quante postazioni cassa?", ph: "es. 1" },
  vendita_prodotti: { type: "bool", label: "Vendita prodotti con barcode" },
  vendita_peso: { type: "bool", label: "Vendita a peso (bilancia)" },
  servizio_tavolo: { type: "bool", label: "Servizio al tavolo" },
  servizio_clienti: {
    type: "choice",
    label: "Come servi i tuoi clienti?",
    required: true,
    options: [
      { value: "banco", label: "Solo al banco o da asporto" },
      { value: "tavoli", label: "Anche ai tavoli" },
      { value: "banco_tavoli", label: "Sia al banco sia ai tavoli" },
    ],
  },
  magazzino: { type: "bool", label: "Gestione magazzino e scorte" },
  fidelity: { type: "bool", label: "Carte fedeltà clienti" },
  appuntamenti: { type: "bool", label: "Agenda appuntamenti" },
  schede_animali: { type: "bool", label: "Schede animali con storico" },
  schede_clienti: { type: "bool", label: "Schede clienti con storico" },
  schede_veicoli: { type: "bool", label: "Schede veicoli con storico interventi" },
  commesse: { type: "bool", label: "Preventivi e commesse di lavoro" },
  foto_prepost: { type: "bool", label: "Foto prima e dopo il servizio" },
  promemoria: { type: "bool", label: "Promemoria WhatsApp / SMS" },
  abbonamenti: { type: "bool", label: "Abbonamenti / pacchetti prepagati" },
  capi_barcode: { type: "bool", label: "Tracciamento capi con barcode" },
  camere_count: { type: "number", label: "Quante camere?", ph: "es. 6" },
  prenotazioni: { type: "bool", label: "Gestione prenotazioni e check-in / check-out" },
  tassa_soggiorno: { type: "bool", label: "Tassa di soggiorno" },
};

const SHOP_SET = ["vendita_prodotti", "magazzino", "fidelity", "postazioni_count"];
const ALIM_SET = ["vendita_prodotti", "vendita_peso", "magazzino", "fidelity", "servizio_tavolo", "postazioni_count"];
const ALIM_BANCO_SET = ["vendita_prodotti", "vendita_peso", "magazzino", "fidelity", "servizio_clienti", "postazioni_count"];
const BEAUTY_SET = ["appuntamenti", "schede_clienti", "promemoria", "fidelity", "abbonamenti", "postazioni_count"];
const OFFICINA_SET = ["appuntamenti", "schede_veicoli", "commesse", "magazzino", "promemoria", "postazioni_count"];

const MODE_QUESTIONS = {
  negozio: SHOP_SET, negozio_animali: SHOP_SET, profumeria: SHOP_SET, ferramenta: SHOP_SET,
  gioielleria: SHOP_SET, fiorista: SHOP_SET, ottica: SHOP_SET, cartoleria: SHOP_SET,
  macelleria: ALIM_SET, panetteria: ALIM_BANCO_SET, pescheria: ALIM_SET, supermercato: ALIM_SET,
  pasticceria: ALIM_BANCO_SET, gelateria: ALIM_BANCO_SET,
  ristorante: ["tavoli_count", "sale_count", "comande_cucina", "comande_bar", "preconto", "tablet_count", "postazioni_count"],
  pizzeria: ["tavoli_count", "sale_count", "comande_cucina", "preconto", "tablet_count", "postazioni_count"],
  bar: ["servizio_clienti", "postazioni_count"],
  toelettatura: ["appuntamenti", "schede_animali", "foto_prepost", "promemoria", "vendita_prodotti", "magazzino", "fidelity", "postazioni_count"],
  parrucchiere: BEAUTY_SET, salone_bellezza: BEAUTY_SET, centro_estetico: BEAUTY_SET,
  lavanderia: ["capi_barcode", "promemoria", "fidelity", "postazioni_count"],
  autoscuola: ["appuntamenti", "schede_clienti", "promemoria", "abbonamenti", "postazioni_count"],
  palestra: ["abbonamenti", "schede_clienti", "promemoria", "fidelity", "postazioni_count"],
  falegnameria: ["commesse", "schede_clienti", "magazzino", "postazioni_count"],
  officina: OFFICINA_SET, officina_meccanica: OFFICINA_SET, carrozzeria: OFFICINA_SET,
  autolavaggio: ["abbonamenti", "fidelity", "postazioni_count"],
  festa: ["comande_cucina", "tablet_count", "postazioni_count"],
  attivita_temporanea: ["vendita_prodotti", "comande_cucina", "tablet_count", "postazioni_count"],
  bnb: ["camere_count", "prenotazioni", "tassa_soggiorno", "postazioni_count"],
};

// Domande extra mostrate SOLO se la condizione sulla risposta è vera
// (es. bar/panificio/pasticceria/gelateria che servono ai tavoli → tavoli/sale/comande/preconto/tablet)
const CONDITIONAL_QUESTIONS = {
  servizio_tavolo: { show: (v) => !!v, questions: ["tavoli_count", "sale_count", "comande_cucina", "comande_bar", "preconto"] },
  servizio_clienti: { show: (v) => v === "tavoli" || v === "banco_tavoli", questions: ["tavoli_count", "sale_count", "comande_cucina", "comande_bar", "preconto", "tablet_count"] },
};

const STEPS = ["Tipo di attività", "La tua attività", "Le tue esigenze", "I tuoi contatti"];

export default function QuoteWizard() {
  const [step, setStep] = useState(1);
  const [group, setGroup] = useState(null);
  const [activity, setActivity] = useState(null);
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({ business_name: "", contact_name: "", email: "", phone: "", city: "", notes: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const effectiveQuestions = () => {
    const base = activity ? (MODE_QUESTIONS[activity.id] || ["postazioni_count"]) : [];
    const out = [];
    for (const k of base) {
      out.push({ key: k, conditional: false });
      const cond = CONDITIONAL_QUESTIONS[k];
      if (cond && cond.show(answers[k])) {
        for (const ck of cond.questions) out.push({ key: ck, conditional: true });
      }
    }
    return out;
  };

  const pickGroup = (g) => { setGroup(g); setActivity(null); setAnswers({}); setStep(2); };
  const pickActivity = (a) => { setActivity(a); setAnswers({}); setStep(3); };

  const setAns = (key, val) => setAnswers((prev) => ({ ...prev, [key]: val }));

  const goContact = () => {
    const missing = effectiveQuestions().find(({ key }) => QUESTION_DEFS[key].required && !answers[key]);
    if (missing) return toast.error(`Rispondi alla domanda: "${QUESTION_DEFS[missing.key].label}"`);
    setStep(4);
  };

  const submit = async () => {
    if (!contact.business_name.trim()) return toast.error("Inserisci il nome della tua attività");
    if (!contact.email.trim() && !contact.phone.trim()) return toast.error("Inserisci almeno email o telefono");
    setSending(true);
    try {
      const answers_readable = (Array.isArray(effectiveQuestions()) ? effectiveQuestions() : [])
        .map((q) => q.key)
        .filter((k) => answers[k] !== undefined && answers[k] !== "" && answers[k] !== false)
        .map((k) => {
          const q = QUESTION_DEFS[k];
          let value;
          if (q.type === "bool") value = "Sì";
          else if (q.type === "choice") value = (q.options.find((o) => o.value === answers[k]) || {}).label || String(answers[k]);
          else value = String(answers[k]);
          return { label: q.label, value };
        });
      await api.post("/quote-requests", {
        group: group.id,
        activity_id: activity.id,
        activity_label: activity.label,
        answers,
        answers_readable,
        ...contact,
      });
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio richiesta — riprova");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <Shell>
        <div className="max-w-lg mx-auto text-center py-20" data-testid="qw-success">
          <CheckCircle2 className="w-16 h-16 text-[#2F9E77] mx-auto mb-6" />
          <h1 className="font-heading text-4xl font-bold tracking-tight mb-4">Richiesta inviata!</h1>
          <p className="text-velta-mute mb-2">
            Grazie <strong className="text-velta-ink">{contact.contact_name || contact.business_name}</strong>.
            Abbiamo ricevuto la tua richiesta per <strong className="text-velta-ink">{activity?.label}</strong>.
          </p>
          <p className="text-velta-mute mb-10">Ti contatteremo al più presto con una proposta su misura per la tua attività.</p>
          <Link to="/" data-testid="qw-back-home" className="inline-flex items-center gap-2 bg-velta-ink text-white px-6 h-12 rounded-md text-sm font-medium hover:bg-[#162244]">
            <ArrowLeft className="w-4 h-4" /> Torna alla home
          </Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      {/* Progress */}
      <div className="max-w-4xl mx-auto mb-10">
        <div className="flex items-center gap-2">
          {(Array.isArray(STEPS) ? STEPS : []).map((s, i) => (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-2 ${i + 1 <= step ? "text-velta-ink" : "text-velta-mute/50"}`}>
                <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-mono font-bold border-2 ${
                  i + 1 < step ? "bg-[#2F9E77] border-[#2F9E77] text-white" : i + 1 === step ? "border-velta-ink" : "border-velta-border"
                }`}>
                  {i + 1 < step ? <Check className="w-3.5 h-3.5" /> : i + 1}
                </span>
                <span className="text-xs font-medium hidden sm:inline">{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-px ${i + 1 < step ? "bg-[#2F9E77]" : "bg-velta-border"}`} />}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="max-w-4xl mx-auto">
        {step === 1 && (
          <div>
            <h1 className="font-heading text-4xl font-bold tracking-tight mb-2">Trova la cassa adatta alla tua attività</h1>
            <p className="text-velta-mute mb-8">Scegli il gruppo che descrive meglio la tua attività.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {(Array.isArray(GROUPS) ? GROUPS : []).map((g) => (
                <button
                  key={g.id}
                  data-testid={`qw-group-${g.id}`}
                  onClick={() => pickGroup(g)}
                  className="text-left border-2 border-velta-border rounded-md p-6 bg-white hover:border-velta-ink transition group"
                >
                  <g.icon className="w-8 h-8 mb-4 text-[#2D5594]" strokeWidth={1.6} />
                  <div className="font-heading font-bold text-lg mb-1">{g.label}</div>
                  <div className="text-xs text-velta-mute leading-relaxed">{g.desc}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && group && (
          <div>
            <BackBtn onClick={() => setStep(1)} />
            <h1 className="font-heading text-4xl font-bold tracking-tight mb-2">{group.label}</h1>
            <p className="text-velta-mute mb-8">Qual è la tua attività?</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {(Array.isArray(ACTIVITIES[group.id]) ? ACTIVITIES[group.id] : []).map((a) => (
                <button
                  key={a.id}
                  data-testid={`qw-activity-${a.id}`}
                  onClick={() => pickActivity(a)}
                  className="text-left border-2 border-velta-border rounded-md p-4 bg-white hover:border-velta-ink transition flex items-center gap-3"
                >
                  <a.icon className="w-6 h-6 text-[#2D5594] shrink-0" strokeWidth={1.6} />
                  <span className="font-medium">{a.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 3 && activity && (
          <div className="max-w-3xl">
            <BackBtn onClick={() => setStep(2)} />
            <h1 className="font-heading text-4xl font-bold tracking-tight mb-2">{activity.label}</h1>
            <p className="text-velta-mute mb-8">Raccontaci le tue esigenze: prepariamo una proposta su misura.</p>
            <div className="space-y-3" data-testid="qw-questions">
              {(Array.isArray(effectiveQuestions()) ? effectiveQuestions() : []).map(({ key: k, conditional }) => {
                const q = QUESTION_DEFS[k];
                if (q.type === "choice") {
                  return (
                    <div key={k} className="border-2 border-velta-border rounded-md p-4 bg-white">
                      <div className="font-medium mb-3">
                        {q.label} <span className="text-velta-error">*</span>
                      </div>
                      <div className="grid sm:grid-cols-3 gap-2">
                        {(Array.isArray(q.options) ? q.options : []).map((o) => {
                          const sel = answers[k] === o.value;
                          return (
                            <button
                              key={o.value}
                              data-testid={`qw-q-${k}-${o.value}`}
                              onClick={() => setAns(k, o.value)}
                              className={`text-sm text-left border-2 rounded-md px-3 py-3 flex items-center gap-2 transition ${
                                sel ? "border-velta-ink bg-velta-ink/5 font-semibold" : "border-velta-border hover:border-velta-ink/50"
                              }`}
                            >
                              <span className={`w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center ${
                                sel ? "border-[#2F9E77]" : "border-velta-border"
                              }`}>
                                {sel && <span className="w-2 h-2 rounded-full bg-[#2F9E77]" />}
                              </span>
                              {o.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                }
                if (q.type === "bool") {
                  const on = !!answers[k];
                  return (
                    <button
                      key={k}
                      data-testid={`qw-q-${k}`}
                      onClick={() => setAns(k, !on)}
                      className={`w-full text-left border-2 rounded-md p-4 flex items-center justify-between transition ${
                        conditional ? "ml-6 w-[calc(100%-1.5rem)]" : ""
                      } ${on ? "border-velta-ink bg-velta-ink/5" : "border-velta-border bg-white hover:border-velta-ink/50"}`}
                    >
                      <span className="font-medium">{q.label}</span>
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${
                        on ? "bg-[#2F9E77] border-[#2F9E77]" : "border-velta-border"
                      }`}>
                        {on && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
                      </span>
                    </button>
                  );
                }
                return (
                  <div key={k} className={`border-2 border-velta-border rounded-md p-4 bg-white flex items-center justify-between gap-4 ${conditional ? "ml-6" : ""}`}>
                    <span className="font-medium">{q.label}</span>
                    <input
                      data-testid={`qw-q-${k}`}
                      type="number"
                      min="0"
                      placeholder={q.ph}
                      value={answers[k] ?? ""}
                      onChange={(e) => setAns(k, e.target.value)}
                      className="w-28 h-11 px-3 border border-velta-border rounded-md text-center font-mono outline-none focus:border-velta-ink"
                    />
                  </div>
                );
              })}
            </div>
            <button
              data-testid="qw-next-contact"
              onClick={goContact}
              className="mt-8 w-full h-12 bg-velta-ink text-white rounded-md font-medium hover:bg-[#162244] inline-flex items-center justify-center gap-2"
            >
              Continua <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {step === 4 && (
          <div className="max-w-3xl">
            <BackBtn onClick={() => setStep(3)} />
            <h1 className="font-heading text-4xl font-bold tracking-tight mb-2">Come ti contattiamo?</h1>
            <p className="text-velta-mute mb-8">Nessun listino, nessun vincolo: ti richiamiamo noi con una proposta dedicata.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Nome attività *" testid="qw-business" value={contact.business_name} onChange={(v) => setContact({ ...contact, business_name: v })} ph="Es. Ferramenta Rossi" />
              <Field label="Il tuo nome" testid="qw-contact" value={contact.contact_name} onChange={(v) => setContact({ ...contact, contact_name: v })} ph="Mario Rossi" />
              <Field label="Email" testid="qw-email" type="email" value={contact.email} onChange={(v) => setContact({ ...contact, email: v })} ph="mario@esempio.it" />
              <Field label="Telefono" testid="qw-phone" value={contact.phone} onChange={(v) => setContact({ ...contact, phone: v })} ph="+39 340 1234567" />
              <Field label="Città" testid="qw-city" value={contact.city} onChange={(v) => setContact({ ...contact, city: v })} ph="Milano" />
            </div>
            <div className="mt-4">
              <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">Note (opzionale)</label>
              <textarea
                data-testid="qw-notes"
                rows={3}
                value={contact.notes}
                onChange={(e) => setContact({ ...contact, notes: e.target.value })}
                placeholder="Raccontaci qualcosa in più sulla tua attività…"
                className="w-full p-3 border border-velta-border rounded-md text-sm outline-none focus:border-velta-ink resize-none bg-white"
              />
            </div>
            <button
              data-testid="qw-submit"
              onClick={submit}
              disabled={sending}
              className="mt-6 w-full h-12 bg-[#2F9E77] hover:bg-[#27855F] text-white rounded-md font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Send className="w-4 h-4" /> {sending ? "Invio in corso…" : "Invia richiesta di preventivo"}
            </button>
            <p className="text-[11px] text-velta-mute text-center mt-3">Riceverai una proposta personalizzata. Nessun pagamento richiesto ora.</p>
          </div>
        )}
      </div>
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div className="min-h-screen bg-velta-bg">
    <header className="h-16 bg-white border-b border-velta-border px-6 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2.5">
        <svg width="24" height="31" viewBox="0 0 100 130" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M50 2 L92 32 L92 98 L50 128 L8 98 L8 32 Z" fill="none" stroke="#0F1F3D" strokeWidth="4" />
          <path d="M28 38 L50 96 L72 38 L62 38 L50 75 L38 38 Z" fill="#2D5594" />
        </svg>
        <div className="leading-tight">
          <div className="font-heading font-extrabold tracking-tight text-sm text-velta-ink">VELTA</div>
          <div className="font-heading font-medium text-[10px] tracking-[0.2em] text-[#2D5594]">SISTEMI</div>
        </div>
      </Link>
      <Link to="/login" className="text-sm font-medium text-velta-ink hover:text-[#2D5594]">Sei già cliente? Accedi</Link>
    </header>
    <main className="px-6 py-12">{children}</main>
  </div>
);

const BackBtn = ({ onClick }) => (
  <button data-testid="qw-back" onClick={onClick} className="inline-flex items-center gap-1.5 text-sm text-velta-mute hover:text-velta-ink mb-4">
    <ArrowLeft className="w-4 h-4" /> Indietro
  </button>
);

const Field = ({ label, value, onChange, ph, testid, type = "text" }) => (
  <div>
    <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">{label}</label>
    <input
      data-testid={testid}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={ph}
      className="w-full h-11 px-3 border border-velta-border rounded-md outline-none focus:border-velta-ink bg-white"
    />
  </div>
);
